import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { Container, Card, Alert } from 'react-bootstrap';
import {
  ProfilePage,
  ProfileDetails,
} from '../components/settings/profile-page';
import { UserProfileUIStore } from '../stores/user-profile-ui-store';
import { profileSaveErrorMessage } from '../utils/profileSaveError';
import { UserStore } from '../stores/user-store';
import { LoadingStore } from '../stores/loading-store';

interface ProfileContainerProps {
  userProfileUIStore?: UserProfileUIStore;
  userStore?: UserStore;
  loadingStore?: LoadingStore;
}

interface ProfileContainerState {
  successMessage: string | null;
  errorMessage: string | null;
}

/**
 * "Profile" — the practitioner's own identity, on its own page under the
 * Account menu.
 *
 * Prepared By and the custom logo passed through here on their way out of
 * Settings and have since moved on to Report Settings, which is where the rest
 * of a report's appearance is configured — so the logo handlers went with them.
 * What is left is the name shown in the Account menu, the two Create Account
 * questions, and the sign-in email.
 */
@inject('userProfileUIStore', 'userStore', 'loadingStore')
@observer
export class ProfileContainer extends React.Component<
  ProfileContainerProps,
  ProfileContainerState
> {
  state: ProfileContainerState = {
    successMessage: null,
    errorMessage: null,
  };

  componentDidMount() {
    const { userProfileUIStore, userStore } = this.props;

    if (!userStore?.currentUser?.subject) {
      userStore?.loadCurrentUser();
    }

    if (!userProfileUIStore?.currentUserProfile) {
      userProfileUIStore?.loadCurrentUserProfile();
    }
  }

  // The page hands over its field values here rather than writing them to the
  // shared profile as they are typed: the Account menu reads that profile, so a
  // half-typed name would show up there and then vanish on the next reload.
  handleSaveProfile = async (details: ProfileDetails) => {
    const { userProfileUIStore } = this.props;
    if (!userProfileUIStore?.currentUserProfile) return;

    try {
      userProfileUIStore.updateName(details.firstName, details.lastName);
      userProfileUIStore.updateSignUpAnswers(
        details.practitionerType,
        details.referralSource,
      );
      await userProfileUIStore.saveCurrentUserProfile();
      this.setState({
        successMessage: 'Profile saved successfully!',
        errorMessage: null,
      });
    } catch (error) {
      console.error('Error saving profile:', error);
      this.setState({
        errorMessage: profileSaveErrorMessage(error, 'Error saving profile.'),
        successMessage: null,
      });
    }
  };

  /**
   * The lab-integration checkbox saves ON TOGGLE, not on a Save button.
   *
   * It is not a preference, it is consent for an outside system to write into
   * this practitioner's charts, and both directions have to be true the moment
   * the box is left: a tick that was never saved means results silently never
   * arrive, and an untick that was never saved means a revocation the
   * practitioner believes in and the server has never heard of. The second is
   * the one that matters, so the save cannot wait for anything.
   *
   * A failed save puts the box BACK. Leaving it showing the new state would be
   * the same lie in a quieter form — the alert says it failed, the checkbox
   * says it worked, and the checkbox is what they will remember.
   */
  handleToggleLabIntegration = async (
    integration: string,
    enabled: boolean,
  ) => {
    const { userProfileUIStore } = this.props;
    if (!userProfileUIStore?.currentUserProfile) return;

    userProfileUIStore.updateLabIntegration(integration, enabled);

    try {
      await userProfileUIStore.saveCurrentUserProfile();
      this.setState({
        successMessage: enabled
          ? 'Virtual Clinic can now send test results to this account.'
          : 'Virtual Clinic can no longer send test results to this account.',
        errorMessage: null,
      });
    } catch (error) {
      console.error('Error saving lab integration setting:', error);
      userProfileUIStore.updateLabIntegration(integration, !enabled);
      this.setState({
        errorMessage: profileSaveErrorMessage(
          error,
          enabled
            ? 'Could not turn the Virtual Clinic integration on. Nothing was changed.'
            : 'Could not turn the Virtual Clinic integration off. Nothing was changed, and Virtual Clinic can still send results to this account.',
        ),
        successMessage: null,
      });
    }
  };

  dismissAlert = (type: 'success' | 'error') => {
    if (type === 'success') {
      this.setState({ successMessage: null });
    } else {
      this.setState({ errorMessage: null });
    }
  };

  render() {
    const { userProfileUIStore } = this.props;
    const { successMessage, errorMessage } = this.state;

    if (!userProfileUIStore?.currentUserProfile) {
      return (
        <Container className="mt-4">
          <Card>
            <Card.Body>
              <div className="text-center">
                <p>Loading user profile...</p>
              </div>
            </Card.Body>
          </Card>
        </Container>
      );
    }

    return (
      <Container fluid className="mt-4">
        <Card>
          <Card.Header>Profile</Card.Header>
          <Card.Body>
            {successMessage && (
              <Alert
                variant="success"
                dismissible
                onClose={() => this.dismissAlert('success')}
              >
                <div className="text-center m-2">{successMessage}</div>
              </Alert>
            )}

            {errorMessage && (
              <Alert
                variant="danger"
                dismissible
                onClose={() => this.dismissAlert('error')}
              >
                <div className="text-center m-2">{errorMessage}</div>
              </Alert>
            )}

            <ProfilePage
              userProfile={userProfileUIStore.currentUserProfile}
              onSaveProfile={this.handleSaveProfile}
              onToggleLabIntegration={this.handleToggleLabIntegration}
            />
          </Card.Body>
        </Card>
      </Container>
    );
  }
}
